import { Form, Modal, Popconfirm, message } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CloseOutlined } from '@ant-design/icons';
import { usePostQuickMap } from '@api/emp/queryHooks';
import EventExplore from '@components/quick-map/components/EventExplore';
import ReadyToMap from '@components/quick-map/components/ReadyToMap';
import { ERuleTip } from '@components/quick-map/components/ReadyToMap/ReadyToMapItem';
import { MAXIMUM_EVENTS_LIMIT } from '@components/quick-map/components/Table/DimensionTable';
import { Button } from '@components/quick-map/components/styled';
import styles from '@components/quick-map/index.less';
import QuickMapIcon from '@icons/emp/quick_map_icon.svg';
import { useMainStore } from '@src/pages/main/store';

export const QUICK_MAP_MODAL_KEY = 'QUICK_MAP_MODAL_KEY';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  font-family: 'Roboto', sans-serif;
  font-style: normal;
`;

const TopPanel = styled.div`
  width: 100%;
  padding: 8px 24px 16px 24px;
  color: #494949;
`;

const BottomPanel = styled.div`
  height: calc(100% - 46px);
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const ErrorPrompt = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 8px;
  margin-right: 8px;
  background: #fff3f3;
  border-radius: 4px;
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.01em;
  color: #d0021b;
  opacity: 0;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 500;
  color: black;
  font-size: 20px;
`;

const QuickMapTitle: React.FC = () => {
  return (
    <TitleWrapper data-id="quick_map_entry_btn">
      <QuickMapIcon style={{ fontSize: '24px', fill: '#000', marginRight: '10px' }} />
      Quick Map
    </TitleWrapper>
  );
};

const QuickMap: React.FC = () => {
  const [visibleKey, closeModal] = useMainStore((state) => [state.visibleKey, state.closeModal]);
  const [form] = Form.useForm();
  const fieldsValue = Form.useWatch('events', form) ?? [];

  const [selectedList, setSelectedList] = useState<string[]>([]);
  const [repeatedNames, setRepeatedNames] = useState<string[]>([]);
  const [disabledSubmit, setDisabledSubmit] = useState<boolean>(false);

  const { mutate: postQuickMap, isLoading: postQuickMapLoading } = usePostQuickMap();

  const exceedSelectedEventsLimit = useMemo(() => {
    return fieldsValue.length >= MAXIMUM_EVENTS_LIMIT;
  }, [fieldsValue]);

  const validator = useCallback(async () => {
    try {
      await form.validateFields();
    } catch (e) {
      setDisabledSubmit(true);
      return;
    }
    setDisabledSubmit(false);
  }, [form]);

  useEffect(() => {
    if (fieldsValue.length === 0) {
      setDisabledSubmit(true);
    } else {
      validator();
    }
  }, [fieldsValue, validator]);

  useEffect(() => {
    const fields = selectedList.map((eventName) => {
      const findField = fieldsValue.find((field) => field.raw_event === eventName);
      if (findField) {
        return findField;
      }
      return {
        raw_event: eventName,
        event_name: eventName,
        event_category: 'Custom',
      };
    });
    form.setFieldsValue({ events: fields });
  }, [selectedList]);

  useEffect(() => {
    const newSelectedList = fieldsValue.map((field) => {
      return field.raw_event;
    });
    setSelectedList(newSelectedList);
  }, [fieldsValue]);

  const resetInitialState = () => {
    setRepeatedNames([]);
    setSelectedList([]);
  };

  const handleCloseModal = () => {
    closeModal();
  };

  const handleCancelModal = () => {
    handleCloseModal();
    resetInitialState();
  };

  const handleOkModal = () => {
    const fields = form.getFieldsValue();
    postQuickMap(fields, {
      onSuccess: (data) => {
        handleCancelModal();
        message.success(data.msg);
      },
      onError: (error: any) => {
        const res = error?.response?.data;
        if (res?.msg === 'Repeated name' && Array.isArray(res?.data)) {
          setRepeatedNames(res?.data);
        }
        message.error({
          content: res?.msg,
          style: {
            marginTop: '40vh',
          },
        });
      },
    });
  };

  const handleUseSelectedChange = useCallback(
    (target, selectedList) => {
      setSelectedList(selectedList ?? []);
    },
    [setSelectedList],
  );

  const CloseWithConfirm = () => {
    if (selectedList.length) {
      return (
        <Popconfirm
          overlayClassName={styles.popconfirm}
          title="You have unsaved changes, are you sure you want to leave?"
          okButtonProps={{ type: 'default', className: styles.okBtn }}
          okText="Stay"
          cancelButtonProps={{ danger: true }}
          cancelText="Leave without saving"
          placement="bottomRight"
          onCancel={handleCancelModal}
        >
          <CloseOutlined />
        </Popconfirm>
      );
    }
    return <CloseOutlined onClick={handleCancelModal} />;
  };

  return (
    <Modal
      className={styles.quickMapModal}
      title={<QuickMapTitle />}
      open={visibleKey === QUICK_MAP_MODAL_KEY}
      width="90%"
      style={{
        top: 20,
      }}
      bodyStyle={{
        padding: 0,
        height: 'calc(100vh - 150px)',
      }}
      forceRender
      destroyOnClose={true}
      footer={[
        <ErrorPrompt
          key={ERuleTip.EVENT_REACHED_SELECT_LIMIT}
          style={exceedSelectedEventsLimit ? { opacity: 1 } : {}}
        >
          {ERuleTip.EVENT_REACHED_SELECT_LIMIT}
        </ErrorPrompt>,
        <Button key={'Cancel'} onClick={handleCancelModal}>
          Cancel
        </Button>,
        <Button
          key={'Map Selected Events'}
          color={'#0046FF'}
          bordercolor={'#0046FF'}
          onClick={handleOkModal}
          loading={postQuickMapLoading}
          disabled={disabledSubmit}
          style={disabledSubmit ? { opacity: 0.4 } : {}}
        >
          Map Selected Events
        </Button>,
      ]}
      maskClosable={false}
      closeIcon={<CloseWithConfirm />}
    >
      <Layout>
        <TopPanel>
          Bulk rename raw events and convert them to mapped events without adding mapping rules.
          Please select, rename and set up a category.
        </TopPanel>
        <BottomPanel>
          <EventExplore selectedList={selectedList} onSelectedChange={handleUseSelectedChange} />
          <ReadyToMap form={form} repeatedNames={repeatedNames} />
        </BottomPanel>
      </Layout>
    </Modal>
  );
};

export default QuickMap;
